import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['modal', 'formSection', 'modalTitle', 'successModal', 'successDetails']
  static values = {
    opportunityId: String,
    payNowTitle: String
  }

  connect() {
    this.checkPastDuePayments()
    this.boundCardAddedHandler = this.handleCardAdded.bind(this)
  }

  disconnect() {
    this.element.removeEventListener('credit-card-form:card-added', this.boundCardAddedHandler)
  }

  getLocale() {
    return document.documentElement.lang || 'en'
  }

  showModal() {
    if (!this.hasModalTarget) return

    this.modalTarget.classList.add('customer-portal-modal', 'show')
    this.modalTarget.setAttribute('aria-modal', 'true')
    this.modalTarget.removeAttribute('aria-hidden')

    if (!document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="addCardModal"]')) {
      const backdrop = document.createElement('div')
      backdrop.className = 'customer-portal-modal-backdrop modal-backdrop fade show'
      backdrop.dataset.backdropFor = 'addCardModal'
      backdrop.addEventListener('click', () => this.closeModal())
      document.body.appendChild(backdrop)
    }
    document.body.classList.add('modal-open')
  }

  openModal() {
    this.showModal()
    this.loadCardForm()
  }

  loadCardForm() {
    if (!this.hasFormSectionTarget) return

    const locale = this.getLocale()
    const url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/add_card_form`

    fetch(url, {
      headers: {
        'Accept': 'text/html'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        this.formSectionTarget.innerHTML = html

        // Show inline form if it's hidden (for original portal non-modal version)
        if (this.formSectionTarget.style.display === 'none') {
          this.formSectionTarget.style.display = 'block'
        }

        this.element.addEventListener('credit-card-form:card-added', this.boundCardAddedHandler)

        // The injected form carries data-controller="credit-card-form", whose
        // connect() mounts Stripe Elements. Do NOT mount again here — a second
        // StripeJS.generateForm() re-mounts onto the same #card-* nodes and
        // leaves the form broken/blank.
      })
      .catch(error => {
        console.error('Error loading form:', error)
        this.formSectionTarget.innerHTML = '<div class="alert alert-danger">Failed to load the form. Please try again.</div>'
      })
  }

  handleCardAdded(event) {
    const cardId = event.detail.id

    if (!cardId) {
      console.error('No card ID received from credit card form')
      return
    }

    this.loadPaymentTermsSelection(cardId)
  }

  loadPaymentTermsSelection(cardId) {
    if (!this.hasFormSectionTarget) return

    const locale = this.getLocale()
    const url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/select_payment_terms?card_id=${cardId}`

    fetch(url, {
      headers: {
        'Accept': 'text/html'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        this.formSectionTarget.innerHTML = html

        this.boundTermsUpdatedHandler = this.handleTermsUpdated.bind(this)
        this.element.addEventListener('payment-term-selector:terms-updated', this.boundTermsUpdatedHandler)
      })
      .catch(error => {
        console.error('Error loading payment terms selection:', error)
        this.formSectionTarget.innerHTML = '<div class="alert alert-danger">Failed to load payment terms. Please try again.</div>'
      })
  }

  handleTermsUpdated() {
    if (this.boundTermsUpdatedHandler) {
      this.element.removeEventListener('payment-term-selector:terms-updated', this.boundTermsUpdatedHandler)
    }

    this.closeModal()
    this.refreshPaymentTerms()
  }

  cleanupForm() {
    if (this.hasFormSectionTarget) {
      this.formSectionTarget.innerHTML = ''
    }
    this.element.removeEventListener('credit-card-form:card-added', this.boundCardAddedHandler)

    if (this.boundTermsUpdatedHandler) {
      this.element.removeEventListener('payment-term-selector:terms-updated', this.boundTermsUpdatedHandler)
    }

    const backdrop = document.querySelector('.modal-backdrop[data-backdrop-for="addCardModal"]')
    if (backdrop) {
      backdrop.remove()
    }
    document.body.classList.remove('modal-open')
  }

  refreshPaymentTerms() {
    // Force a hard reload to reflect payment updates
    // Add timestamp to bust any cache
    const url = new URL(window.location.href)
    url.searchParams.set('_t', Date.now())
    window.location.href = url.toString()
  }

  payNow(event) {
    const paymentTermId = event.detail.paymentTermId

    if (!paymentTermId) {
      console.error('No payment term ID received')
      return
    }

    this.openPayNowModal(paymentTermId)
  }

  openPayNowModal(paymentTermId) {
    if (!this.hasModalTarget) {
      console.error('Modal target not found')
      return
    }

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = this.payNowTitleValue
    }

    this.paymentTermId = paymentTermId
    this.showModal()
    this.loadPayNowForm(paymentTermId)
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('show')
      this.modalTarget.setAttribute('aria-hidden', 'true')
      this.modalTarget.removeAttribute('aria-modal')

      const backdrop = document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="addCardModal"]')
      if (backdrop) {
        backdrop.remove()
      }
      document.body.classList.remove('modal-open')
    } else if (this.hasFormSectionTarget) {
      // For original portal: hide inline form section
      this.formSectionTarget.style.display = 'none'
    }

    this.cleanupForm()
  }

  loadPayNowForm(paymentTermId) {
    if (!this.hasFormSectionTarget) return

    const locale = this.getLocale()
    const url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/pay_now_form?payment_term_id=${paymentTermId}`

    fetch(url, {
      headers: {
        'Accept': 'text/html'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        this.formSectionTarget.innerHTML = html

        this.boundPaymentCompletedHandler = this.handlePaymentCompleted.bind(this)
        this.element.addEventListener('pay-now-form:payment-completed', this.boundPaymentCompletedHandler)

        // Stripe Elements are mounted by the injected credit-card-form
        // controller's connect(); mounting again here double-mounts and breaks
        // the form.
      })
      .catch(error => {
        console.error('Error loading pay now form:', error)
        this.formSectionTarget.innerHTML = '<div class="alert alert-danger">Failed to load payment form. Please try again.</div>'
      })
  }

  handlePaymentCompleted(event) {
    if (!this.hasFormSectionTarget) return

    const paymentTermId = event?.detail?.paymentTermId
    this.lastPaidPaymentTermId = paymentTermId

    if (this.boundPaymentCompletedHandler) {
      this.element.removeEventListener('pay-now-form:payment-completed', this.boundPaymentCompletedHandler)
    }

    this.closeModal()

    setTimeout(() => {
      this.showSuccessModal()
    }, 300)
  }

  showSuccessModal() {
    if (!this.hasSuccessModalTarget) return

    this.fetchPaymentSummary()
      .then(() => {
        this.successModalTarget.classList.add('customer-portal-modal', 'show')
        this.successModalTarget.setAttribute('aria-modal', 'true')
        this.successModalTarget.removeAttribute('aria-hidden')

        if (!document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="paymentSuccessModal"]')) {
          const backdrop = document.createElement('div')
          backdrop.className = 'customer-portal-modal-backdrop modal-backdrop fade show'
          backdrop.dataset.backdropFor = 'paymentSuccessModal'
          backdrop.addEventListener('click', () => {
            this.closeSuccessModal()
          })
          document.body.appendChild(backdrop)
        }
        document.body.classList.add('modal-open')
      })
      .catch(error => {
        console.error('Error showing success modal:', error)
        this.refreshPaymentTerms()
      })
  }

  closeSuccessModal() {
    if (!this.hasSuccessModalTarget) return

    this.successModalTarget.classList.remove('show')
    this.successModalTarget.setAttribute('aria-hidden', 'true')
    this.successModalTarget.removeAttribute('aria-modal')

    const backdrop = document.querySelector('.customer-portal-modal-backdrop[data-backdrop-for="paymentSuccessModal"]')
    if (backdrop) {
      backdrop.remove()
    }
    document.body.classList.remove('modal-open')

    // Delay to ensure backend has fully processed the payment before refreshing
    // Using 1.5 seconds to account for async payment processing
    setTimeout(() => {
      this.refreshPaymentTerms()
    }, 1500)
  }

  async fetchPaymentSummary() {
    if (!this.hasSuccessDetailsTarget) return

    const locale = this.getLocale()
    let url = `/${locale}/oa/opportunities/${this.opportunityIdValue}/payment_terms/payment_summary`

    if (this.lastPaidPaymentTermId) {
      url += `?payment_term_id=${this.lastPaidPaymentTermId}`
    }

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Payment summary error response:', errorText)
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()

      if (html.trim().length === 0) {
        console.warn('Empty HTML received from payment summary')
        this.successDetailsTarget.innerHTML = '<p class="text-muted">Payment successful!</p>'
      } else {
        this.successDetailsTarget.innerHTML = html
      }
    } catch (error) {
      console.error('Error fetching payment summary:', error)
      this.successDetailsTarget.innerHTML = '<p class="text-muted">Payment successful!</p>'
    }
  }

  checkPastDuePayments() {
    const paymentDetailsDiv = document.querySelector('.opportunity-payment-terms')
    if (!paymentDetailsDiv) return

    // Check for past due or pending first payment terms
    const chargeableTerms = this.element.querySelectorAll('[data-controller*="chargeable-payment-term"]')
    let hasPastDue = false

    chargeableTerms.forEach((term, index) => {
      const dueDateValue = term.dataset.chargeablePaymentTermDueDateValue
      const dueDate = new Date(dueDateValue)
      const now = new Date()

      // Check if past due
      if (dueDate <= now && index === 0) {
        hasPastDue = true
      }
    })

    if (hasPastDue) {
      paymentDetailsDiv.classList.add('past-due')
      // Auto-open the payment terms section
      const header = paymentDetailsDiv.querySelector('.opportunity-details-header')
      const content = paymentDetailsDiv.querySelector('.opportunity-details-content')
      if (header && content) {
        header.classList.remove('closed')
        header.classList.add('opened')
        content.classList.remove('closed')
        content.classList.add('opened')
      }
    }
  }
}
